import { z } from 'zod';
import { Sha256, Uuid, VideoFormat } from './common.ts';
import { VideoBrief } from './content.ts';
import { RevisionMeta } from './entities.ts';
import { NotebookUrl } from './agent-team.ts';

export const ScriptLibraryEntry = z.object({
  document_id: Uuid, workspace_id: Uuid, channel_id: Uuid,
  title: z.string().trim().min(1).max(200), format: VideoFormat, brief: VideoBrief,
  status: z.enum(['active', 'archived']), metadata_version: z.int().min(1),
  head_revision: RevisionMeta.nullable(), preview: z.string().max(300), updated_at: z.iso.datetime({ offset: true }),
  revision_count: z.int().min(0), has_text: z.boolean(),
  legacy_project_id: Uuid.nullable(),
});
export type ScriptLibraryEntry = z.infer<typeof ScriptLibraryEntry>;

export const ScriptUsageProject = z.object({
  project_id: Uuid, title: z.string().min(1).max(200), format: VideoFormat,
  status: z.enum(['active', 'archived']), source_project: z.object({id:Uuid,title:z.string().min(1).max(200)}).nullable(),
  source_revision: z.object({id:Uuid,number:z.int().min(1)}).nullable(),
  views: z.number().min(0).nullable(), views_covered: z.int().min(0).nullable(),
  publication_count: z.int().min(0).nullable(), platforms: z.array(z.string().max(40)),
});
export type ScriptUsageProject = z.infer<typeof ScriptUsageProject>;
export const ScriptUsage = z.object({
  document_id: Uuid, projects: z.array(ScriptUsageProject).max(20), truncated: z.boolean(), metrics_visible: z.boolean(),
});
export type ScriptUsage = z.infer<typeof ScriptUsage>;

export const ScriptAssignment = z.object({
  id: Uuid, workspace_id: Uuid, project_id: Uuid, document_id: Uuid, revision_id: Uuid,
  working_document_id: Uuid, working_revision_id: Uuid, assigned_at: z.iso.datetime({ offset: true }),
});
export type ScriptAssignment = z.infer<typeof ScriptAssignment>;

/** An observed source reference, not an invented NotebookLM source identifier. */
export const ScriptNotebookSourceRef = z.strictObject({
  title: z.string().trim().min(1).max(300), marker: z.string().trim().min(1).max(160).optional(),
});
export type ScriptNotebookSourceRef = z.infer<typeof ScriptNotebookSourceRef>;
export const ScriptSyncStatus = z.enum(['pending', 'confirmed', 'conflict', 'error', 'unknown']);
export const ScriptSyncError = z.enum(['SESSION_REQUIRED', 'QUOTA_EXCEEDED', 'NETWORK_ERROR', 'NOTEBOOK_UI_CHANGED', 'SOURCE_AMBIGUOUS', 'CONTENT_MISMATCH', 'UNKNOWN_DELIVERY']);
export const ScriptNotebookBinding = z.object({
  id: Uuid, workspace_id: Uuid, channel_id: Uuid, document_id: Uuid,
  notebook_url: NotebookUrl, source_ref: ScriptNotebookSourceRef,
  version: z.int().min(1), status: ScriptSyncStatus, direction: z.enum(['import', 'export']).nullable(),
  local_revision_id: Uuid.nullable(), remote_text_hash: Sha256.nullable(),
  confirmed_local_revision_id: Uuid.nullable(), confirmed_remote_text_hash: Sha256.nullable(),
  confirmed_at: z.iso.datetime({ offset: true }).nullable(), error_code: ScriptSyncError.nullable(),
  updated_at: z.iso.datetime({ offset: true }),
});
export type ScriptNotebookBinding = z.infer<typeof ScriptNotebookBinding>;

/** Independent of NotebookLM access granted to the editorial team. */
export const ScriptSyncSettings = z.object({
  workspace_id: Uuid, channel_id: Uuid, version: z.int().min(1), enabled: z.boolean(),
  notebook_url: NotebookUrl.nullable(), auto_confirmed_revisions: z.boolean(),
  updated_at: z.iso.datetime({ offset: true }),
});
export type ScriptSyncSettings = z.infer<typeof ScriptSyncSettings>;
